/**
 * AI Assistant Input
 *
 * Text input for sending messages to the AI Assistant.
 * Uses SSE streaming for real-time responses.
 * Supports voice input via Web Speech API.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { MicIcon, MicOffIcon, SendIcon, StopIcon } from "../../icons";
import { useRootStore } from '../../store/contexts';
import { tokenManager } from '../../api/tokenManager';
import { useAiSearchStream, StreamEvent, StreamState, ToolPayload, DeltaPayload, ExplanationPayload } from './useAiSearchStream';
import { useSpeechRecognition } from './useSpeechRecognition';

export const AiAssistantInput: React.FC = observer(() => {
  const { aiAssistantStore } = useRootStore();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  // Speech recognition for voice input
  // Tracks accumulated final text and current interim text
  const accumulatedTextRef = useRef('');

  const handleSpeechTranscript = useCallback((transcript: string, isFinal: boolean) => {
    if (isFinal) {
      // Add to accumulated text with space separator
      const separator = accumulatedTextRef.current ? ' ' : '';
      accumulatedTextRef.current += separator + transcript;
      setMessage(accumulatedTextRef.current);
    } else {
      // Show accumulated + interim (interim in real-time as user speaks)
      const separator = accumulatedTextRef.current ? ' ' : '';
      setMessage(accumulatedTextRef.current + separator + transcript);
    }
  }, []);

  const handleSpeechError = useCallback((error: string) => {
    aiAssistantStore.handleStreamingError(error);
  }, [aiAssistantStore]);

  const {
    isSupported: isSpeechSupported,
    isListening,
    toggleListening,
    stopListening,
  } = useSpeechRecognition({
    lang: 'es-AR',
    onTranscript: handleSpeechTranscript,
    onError: handleSpeechError,
  });

  // Handle streaming events
  const handleStreamEvent = useCallback((event: StreamEvent) => {
    switch (event.type) {
      case 'tool':
        aiAssistantStore.updateToolStatus(event.payload as ToolPayload);
        break;
      case 'delta':
        aiAssistantStore.updateStreamingContent((event.payload as DeltaPayload).content);
        break;
      case 'explanation':
        aiAssistantStore.setExplanation(event.payload as ExplanationPayload);
        break;
      case 'done':
        // Handled by onComplete callback for full state access (including responseId)
        break;
      case 'error':
        aiAssistantStore.handleStreamingError((event.payload as { message: string }).message);
        break;
    }
  }, [aiAssistantStore]);

  // Handle stream completion with full state (including responseId)
  const handleStreamComplete = useCallback((state: StreamState) => {
    aiAssistantStore.finishStreaming(
      state.conversationId || '',
      state.sources,
      state.responseId
    );
  }, [aiAssistantStore]);

  const { sendMessage: sendStreamMessage, abort } = useAiSearchStream({
    onEvent: handleStreamEvent,
    onComplete: handleStreamComplete,
  });

  // Handle cancel button click
  const handleCancel = useCallback(() => {
    abort();
    aiAssistantStore.cancelStreaming();
  }, [abort, aiAssistantStore]);

  // Focus input when drawer opens
  useEffect(() => {
    if (aiAssistantStore.isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [aiAssistantStore.isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage || aiAssistantStore.isBusy) return;

    // Get valid auth token (handles refresh automatically)
    const token = await tokenManager.getValidAccessToken();
    if (!token) {
      aiAssistantStore.handleStreamingError('Sesión expirada. Por favor, iniciá sesión nuevamente.');
      return;
    }

    // Stop speech recognition if active
    if (isListening) {
      stopListening();
    }

    // Start streaming in the store
    aiAssistantStore.startStreaming(trimmedMessage);
    setMessage('');
    accumulatedTextRef.current = ''; // Clear speech accumulator

    // Send the message via SSE
    await sendStreamMessage(
      trimmedMessage,
      token,
      aiAssistantStore.currentConversationId
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={isListening ? 'Escuchando...' : 'Tu pregunta...'}
        disabled={aiAssistantStore.isBusy}
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {aiAssistantStore.isStreaming ? (
                <Tooltip title="Cancelar" arrow>
                  <IconButton
                    onClick={handleCancel}
                    size="small"
                    color="error"
                    sx={{
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    }}
                  >
                    <StopIcon />
                  </IconButton>
                </Tooltip>
              ) : aiAssistantStore.isLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isSpeechSupported && (
                    <Tooltip title={isListening ? 'Detener' : 'Hablar'} arrow>
                      <IconButton
                        onClick={toggleListening}
                        size="small"
                        sx={{
                          color: isListening ? 'error.main' : 'action.active',
                          backgroundColor: isListening
                            ? alpha(theme.palette.error.main, 0.1)
                            : 'transparent',
                          animation: isListening ? 'pulse 1.5s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.1)' },
                            '100%': { transform: 'scale(1)' },
                          },
                          '&:hover': {
                            backgroundColor: isListening
                              ? alpha(theme.palette.error.main, 0.2)
                              : alpha(theme.palette.action.active, 0.1),
                          },
                        }}
                      >
                        {isListening ? <MicOffIcon /> : <MicIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    type="submit"
                    color="primary"
                    disabled={!message.trim()}
                    edge="end"
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              )}
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            pr: 0.5,
          },
        }}
      />
    </Box>
  );
});

export default AiAssistantInput;
